import { Skill } from "@cohesive/api-client";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  KEY_DOWN_COMMAND,
  LexicalCommand,
  TextNode,
} from "lexical";
import { useCallback, useEffect } from "react";

import { INSERT_TEXT_COMMAND } from "../TextReplacePlugin/TextReplacePlugin";
import { $createSkillNode, SkillNode, SkillPayload } from "./SkillNode";

export type InsertSkillPayload = Readonly<SkillPayload>;

export const INSERT_SKILL_COMMAND: LexicalCommand<SkillPayload> =
  createCommand();

export const TRIGGER_SKILL_MENU_COMMAND: LexicalCommand<{ position: number }> =
  createCommand();

type SkillPluginProps = {
  enabled?: boolean;
  skills: Skill[];
  enableTrigger?: boolean;
};

export function SkillPlugin({
  enabled,
  skills: _skills,
  enableTrigger = true,
}: SkillPluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([SkillNode])) {
      throw new Error("SkillNode not registered on editor");
    }
  }, [editor]);

  const textTransform = useCallback((textNode: TextNode) => {
    const textContent = textNode.getTextContent();

    // Regex to match text that is wrapped in curly braces
    const match = /\{skill:([^}]*):([^}]*)\}/i.exec(textContent);

    if (!match) return;

    const [, skillId, skillName] = match || [];

    const skillNode = $createSkillNode({
      id: skillId,
      name: skillName,
    });

    // If full text content matches, we can replace existing textNode
    // otherwise we need to split up the text node based on match index
    if (match[0] === textContent) {
      textNode.replace(skillNode);
      return;
    }

    const startIndex = match.index || 0;
    const endIndex = startIndex + match[0].length;

    if (startIndex === 0) {
      const [currentNode] = textNode.splitText(endIndex);
      currentNode?.replace(skillNode);
    } else {
      const [, currentNode] = textNode.splitText(startIndex, endIndex);
      currentNode?.replace(skillNode);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    return editor.registerNodeTransform(TextNode, textTransform);
  }, [editor, enabled, textTransform]);

  useEffect(() => {
    if (!enabled) return;

    const insertSkillNode = (payload: SkillPayload) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const skillNode = $createSkillNode(payload);

          // Get the anchor node (where cursor is)
          const anchorNode = selection.anchor.getNode();

          // Check if it's a text node and ends with /
          if (anchorNode instanceof TextNode) {
            const text = anchorNode.getTextContent();
            if (text.endsWith("/")) {
              anchorNode.setTextContent(text.slice(0, -1));
            }
          }

          // Insert node at cursor
          selection.insertNodes([skillNode, $createTextNode(" ")]);
        }
      });

      return true;
    };

    // Registering custom command that will be handled by this plugin
    return editor.registerCommand(
      INSERT_SKILL_COMMAND,
      insertSkillNode,
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, enabled]);

  // Register / keypress detection to trigger skill menu
  useEffect(() => {
    if (!enabled || !enableTrigger) return;

    const handleKeyDown = (e: KeyboardEvent): boolean => {
      if (e.key === "/") {
        let shouldTrigger = false;

        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const anchorOffset = selection.anchor.offset;

            if (anchorNode instanceof TextNode) {
              const textContent = anchorNode.getTextContent();
              const charBeforeCursor = textContent[anchorOffset - 1];

              shouldTrigger =
                !charBeforeCursor ||
                charBeforeCursor === " " ||
                charBeforeCursor === "\n";
            } else {
              shouldTrigger = true;
            }
          }
        });

        if (shouldTrigger) {
          const fullTextContent = editor.getRootElement()?.textContent || "";
          e.preventDefault();
          editor.dispatchCommand(INSERT_TEXT_COMMAND, { text: "/" });
          editor.dispatchCommand(TRIGGER_SKILL_MENU_COMMAND, {
            position: fullTextContent.length,
          });
          return true;
        }
      }

      return false;
    };

    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      handleKeyDown,
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, enabled, enableTrigger]);

  return null;
}
